import axios from 'axios';
import { API_BASE } from '@/lib/api';

// The /admin support portal's API client. Its session is separate from
// the app's: its own token, its own storage key, and a 401 sends you to
// the portal's sign-in, not the app's.
const TOKEN_KEY = 'farmAfricaAdminToken';
const ADMIN_KEY = 'farmAfricaAdmin';

export const adminSession = {
  token: () => localStorage.getItem(TOKEN_KEY),
  admin: () => {
    try {
      return JSON.parse(localStorage.getItem(ADMIN_KEY) || 'null');
    } catch {
      return null;
    }
  },
  save: ({ access_token: token, admin }) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ADMIN_KEY);
  },
};

const adminApi = axios.create({
  baseURL: `${API_BASE}/admin`,
  headers: { 'Content-Type': 'application/json' },
});

adminApi.interceptors.request.use((config) => {
  const token = adminSession.token();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

adminApi.interceptors.response.use(
  (res) => res,
  (error) => {
    const url = error.config?.url || '';
    if (error.response?.status === 401 && !url.startsWith('/auth/')) {
      adminSession.clear();
      if (window.location.pathname !== '/admin') window.location.assign('/admin');
    }
    return Promise.reject(error);
  },
);

export const adminAPI = {
  login: (email, password) => adminApi.post('/auth/login', { email, password }),
  checkInvite: (token) => adminApi.post('/auth/invite/check', { token }),
  acceptInvite: (token, name, password) => adminApi.post('/auth/invite/accept', { token, name, password }),
  // Step 2 of signing in: the code from the authenticator app (or a recovery code).
  verifyMfa: (ticket, code) => adminApi.post('/auth/mfa', { ticket, code }),
  me: () => adminApi.get('/me'),
  admins: () => adminApi.get('/admins'),
  invite: (email, name) => adminApi.post('/admins', { email, name: name || null }),
  resendInvite: (id) => adminApi.post(`/admins/${id}/resend-invite`),
  setAdminAccess: (id, on) => adminApi.post(`/admins/${id}/status`, { status: on ? 'open' : 'closed' }),
  resetAuthenticator: (id) => adminApi.post(`/admins/${id}/reset-authenticator`),
  queue: (params) => adminApi.get('/support/threads', { params }),
  unread: () => adminApi.get('/support/unread'),
  thread: (id, after) => adminApi.get(`/support/threads/${id}/messages`, { params: after ? { after } : undefined }),
  reply: (id, { body, image }) => adminApi.post(`/support/threads/${id}/messages`, { body: body || null, image: image || null }),
  read: (id) => adminApi.post(`/support/threads/${id}/read`),
  setStatus: (id, status) => adminApi.post(`/support/threads/${id}/status`, { status }),
  masterList: (params) => adminApi.get('/master-products', { params }),
  masterUpload: (filename, contentBase64, commit) =>
    adminApi.post('/master-products/upload', { filename, content_base64: contentBase64, commit }),
  masterReview: (id, action, note) => adminApi.post(`/master-products/${id}/review`, { action, note: note || null }),
  masterSpray: (id, spray) => adminApi.put(`/master-products/${id}/spray`, spray),
  masterTemplate: () => adminApi.get('/master-products/template', { responseType: 'blob' }),
  clusters: (q) => adminApi.get('/clusters', { params: q ? { q } : undefined }),
  createCluster: (data) => adminApi.post('/clusters', data),
  updateCluster: (id, data) => adminApi.patch(`/clusters/${id}`, data),
  smsGuard: () => adminApi.get('/sms-guard'),
  resumeSms: () => adminApi.post('/sms-guard/resume'),
};

export function apiError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg.replace(/^Value error, /, '');
  return fallback;
}
